import { ButtonType, DialogFooterButtonsWidget, InlineAlertType, InlineAlertWidget, TextAreaWidget, TextFieldWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { CloseAndDismiss } from '~src/commonInfra/dialogs/ReactModalService';
import { DialogFieldParentStyle, DialogTextFieldStyle } from '~src/commonInfra/dialogs/style/DialogStyles';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { MapDispatchToProps, MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { getExtractEncryptionKeyRequest } from '../actions/GetExtractEncryptionKeyRequestAction';
import { openKMSKeyPolicyDialog } from '../dialogs/KMSKeyPolicyDialog';
import { IGetSiteEncryptionKeyResult } from '../models/ExtractEncryptionModels';
import { RegisterSiteEncryptionKeyRequest } from '../serverApi/requests/RegisterSiteEncryptionKeyRequest';
import { registerSiteEncryptionKey } from '../services/ExtractEncryptionKeyService';
import { extractRegionFromKeyIdentifier } from '../utils/ExtractEncrpytionUtils';

export interface KMSKeyGenerateViewOwnProps {
    isEncryptionEnabled: boolean;
    closeDialog: CloseAndDismiss;
    onCancel: () => void;
}

export interface KMSKeyGenerateViewDispatchProps {
    retrieveExtractEncryptionKey: () => void;
}

export interface KMSKeyGenerateViewStateProps {
    encryptionKey: IGetSiteEncryptionKeyResult;
}

export interface KMSKeyGenerateViewState {
    keyIdentifier: string;
    description: string;
    isRegisteringKey: boolean;
    hasError: boolean;
    errorMessage: string;
}

export type KMSKeyGenerateViewProps = KMSKeyGenerateViewOwnProps & KMSKeyGenerateViewDispatchProps & KMSKeyGenerateViewStateProps;

const MAX_DESC_CHARS = 100;

export class KMSKeyGenerateViewComponent extends React.PureComponent<KMSKeyGenerateViewProps, KMSKeyGenerateViewState> {

    constructor(props: KMSKeyGenerateViewProps) {
        super(props);
        this.state = {
            keyIdentifier: '',
            description: '',
            isRegisteringKey: false,
            hasError: false,
            errorMessage: ''
        };
    }

    private isNextButtonDisabled(): boolean {
        return this.state.keyIdentifier === '' || this.state.description.length > MAX_DESC_CHARS || this.state.hasError;
    }

    private isKeyAlreadyRegistered(): boolean {
        if(!this.props.encryptionKey || !this.props.encryptionKey.rootKeys) return false;
        return this.props.encryptionKey.rootKeys.some(key => key.externalKeyId === this.state.keyIdentifier);
    }

    public render(): JSX.Element {
        return <>
            <div>
                <span>{Messages.extractEncrpytion_generateKMSKeyDialog_Desc()}</span>
                <span>
                    <TextLinkWidget
                        testId={'kms-key-policy-learn-more'}
                        target={'_blank'}
                        href={HelpService.getHelpUrlByCategory('online-kms-external-configure')}
                        className={learnMoreStyle}>
                        {Messages.learnMore()}
                    </TextLinkWidget>
                </span>
            </div>
            <br/>
            <div>
                <TextLinkWidget
                    target={'_blank'}
                    href={Messages.extractEncryption_generateKMSKeyDialog_KMSLink()}
                    type={TextLinkKind.Standalone}
                    >
                    {Messages.extractEncryption_generateKMSKeyDialog_LaunchKMS()}
                </TextLinkWidget>
            </div>
            <br/>
            <div style={DialogFieldParentStyle}>
                <TextFieldWidget
                    kind='line'
                    label={Messages.extractEncryption_generateKMSKeyDialog_KeyID()}
                    onChange={e => this.setState({keyIdentifier: e.target.value, hasError: false, errorMessage: ''})}
                    value={this.state.keyIdentifier}
                    testId='key-identifier-text'
                    valid={this.state.hasError ? false : undefined}
                    message={this.state.errorMessage}
                    className={DialogTextFieldStyle}/>
            </div>
            <div style={DialogFieldParentStyle}>
                <TextAreaWidget
                    label={Messages.descriptionOptional()}
                    valid={this.state.description.length <= MAX_DESC_CHARS ? undefined : false}
                    rows={3}
                    value={this.state.description}
                    onChange={e => this.setState({description: e.target.value})}
                    className={DialogTextFieldStyle}
                    testId='key-description-text'
                    maxLength={MAX_DESC_CHARS}
                    message={Messages.extractEncryption_generateKMSKeyDialog_DescriptionLength({LENGTH: this.state.description.length.toString(), MAX_DESC_CHARS: MAX_DESC_CHARS.toString()})}
                    />
            </div>
            {!this.props.isEncryptionEnabled ? <InlineAlertWidget style={{marginBottom: '12px'}} children={Messages.extractEncrpytionEnableWarning()} testId={`enable-extract-encrpytion-alert`} type={InlineAlertType.Warning} /> : null}
            <div style={{ position: 'relative' }}>
                <DialogFooterButtonsWidget
                    buttons={[
                        {
                            buttonType: ButtonType.Outline,
                            children: Messages.cancel(),
                            onClick: () => {
                                this.props.closeDialog.close();
                                this.props.onCancel();
                            },
                            testId: 'cancel-button'
                        },
                        {
                            buttonType: ButtonType.Primary,
                            showSpinner: this.state.isRegisteringKey,
                            children: this.props.isEncryptionEnabled ? Messages.next() : Messages.extractEncryption_enableEncryption_button(),
                            disabled: this.isNextButtonDisabled(),
                            onClick: () => this.registerKey(),
                            testId: 'next-button'
                        }
                    ]}
                />
            </div>
        </>;
    }

    private registerKey(): void {
        if(this.isKeyAlreadyRegistered()) {
            this.setState({hasError: true, errorMessage: Messages.extractEncryption_generateKMSKeyDialog_KeyAlreadyRegistered()});
            return;
        } else if(extractRegionFromKeyIdentifier(this.state.keyIdentifier) === '') {
            this.setState({hasError: true, errorMessage: Messages.extractEncryption_generateKMSKeyDialog_InvalidKeyID()});
            return;
        }

        this.setState({isRegisteringKey: true}, () => {
            registerSiteEncryptionKey(new RegisterSiteEncryptionKeyRequest({
                keyMetadata: {
                    externalKeyId: this.state.keyIdentifier,
                    keyRegion: extractRegionFromKeyIdentifier(this.state.keyIdentifier),
                    keyDescription: this.state.description
                }
            })).then((result) => {
                if(result.errors){
                    ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncryption_generateKMSKeyDialog_CreateKey_FailureToast() });
                } else {
                    this.props.retrieveExtractEncryptionKey();
                    openKMSKeyPolicyDialog(this.state.keyIdentifier, true);
                }
            }).catch(() => {
                ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncryption_generateKMSKeyDialog_CreateKey_FailureToast() });
            }).finally(() => {
                this.setState({isRegisteringKey: false});
                this.props.closeDialog.close();
            });
        });
    }

}

const mapDispatchToProps: MapDispatchToProps<KMSKeyGenerateViewDispatchProps> =
  (dispatch) => bindActionCreators({
    retrieveExtractEncryptionKey: getExtractEncryptionKeyRequest
  },
  dispatch);

const mapStateToProps: MapStateToProps<KMSKeyGenerateViewStateProps> = (state: AppState) => ({
    encryptionKey: state.extractEncryptionInfo.encryptionKey
});

export const KMSKeyGenerateView: React.ComponentClass<KMSKeyGenerateViewOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(KMSKeyGenerateViewComponent);